<template>
  <div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto" @click.self="closeModal">
    <div class="flex min-h-screen items-center justify-center p-4">
      <div class="relative w-full max-w-md transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 shadow-xl transition-all">
        <!-- Header -->
        <div class="bg-gray-50 dark:bg-gray-700 px-6 py-4 border-b border-gray-200 dark:border-gray-600">
          <div class="flex items-center justify-between">
            <h3 class="text-lg font-medium text-gray-900 dark:text-white">
              {{ title }}
            </h3>
            <button @click="closeModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        <!-- Search Input -->
        <div class="p-4 border-b border-gray-200 dark:border-gray-600">
          <div class="relative">
            <input
              v-model="searchQuery"
              type="text"
              :placeholder="searchPlaceholder"
              class="w-full px-4 py-2 pl-10 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              @input="onSearch"
            />
            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
              </svg>
            </div>
          </div>
        </div>

        <!-- Options List -->
        <div class="max-h-64 overflow-y-auto">
          <div v-if="filteredOptions.length === 0" class="p-4 text-center text-gray-500 dark:text-gray-400">
            {{ searchQuery ? 'Arama sonucu bulunamadı' : 'Seçenek bulunmuyor' }}
          </div>
          <div v-else>
            <button
              v-for="option in filteredOptions"
              :key="getOptionKey(option)"
              @click="selectOption(option)"
              class="w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-600 last:border-b-0 transition-colors"
              :class="{
                'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300': isSelected(option)
              }"
            >
              <div class="flex items-center justify-between">
                <span class="text-sm font-medium text-gray-900 dark:text-white">
                  {{ getOptionLabel(option) }}
                </span>
                <svg v-if="isSelected(option)" class="w-5 h-5 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
              </div>
            </button>
          </div>
        </div>

        <!-- Footer -->
        <div class="bg-gray-50 dark:bg-gray-700 px-6 py-4 border-t border-gray-200 dark:border-gray-600">
          <div class="flex justify-between">
            <button
              v-if="allowClear"
              @click="clearSelection"
              class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-lg transition-colors"
            >
              Temizle
            </button>
            <button
              @click="confirmSelection"
              class="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors ml-auto"
            >
              Seç
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SelectModal',
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      required: true
    },
    options: {
      type: Array,
      default: () => []
    },
    selectedValue: {
      type: [String, Number, Object],
      default: null
    },
    labelKey: {
      type: String,
      default: 'label'
    },
    valueKey: {
      type: String,
      default: 'value'
    },
    searchPlaceholder: {
      type: String,
      default: 'Arama yapın...'
    },
    allowClear: {
      type: Boolean,
      default: true
    },
    searchable: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      searchQuery: '',
      filteredOptions: []
    }
  },
  watch: {
    options: {
      handler() {
        this.updateFilteredOptions()
      },
      immediate: true
    },
    searchQuery() {
      this.updateFilteredOptions()
    }
  },
  methods: {
    getOptionLabel(option) {
      if (typeof option === 'string') return option
      return option[this.labelKey] || option.name || option.label || String(option)
    },
    getOptionKey(option) {
      if (typeof option === 'string') return option
      return option[this.valueKey] || option.id || option.value || String(option)
    },
    isSelected(option) {
      if (!this.selectedValue) return false
      const optionKey = this.getOptionKey(option)
      if (typeof this.selectedValue === 'object') {
        return this.getOptionKey(this.selectedValue) === optionKey
      }
      return this.selectedValue === optionKey
    },
    selectOption(option) {
      this.$emit('select', option)
    },
    confirmSelection() {
      this.$emit('confirm', this.selectedValue)
      this.closeModal()
    },
    clearSelection() {
      this.$emit('clear')
      this.closeModal()
    },
    closeModal() {
      this.searchQuery = ''
      this.$emit('close')
    },
    onSearch() {
      this.$emit('search', this.searchQuery)
    },
    updateFilteredOptions() {
      if (!this.searchQuery || !this.searchable) {
        this.filteredOptions = [...this.options]
        return
      }
      
      const query = this.searchQuery.toLowerCase()
      this.filteredOptions = this.options.filter(option => {
        const label = this.getOptionLabel(option).toLowerCase()
        return label.includes(query)
      })
    }
  }
}
</script>
